import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { ReportDoc, api } from "../api";

interface ReportContextValue {
  slug: string | null;
  report: ReportDoc | null;
  loading: boolean;
  error: string | null;
  /** Tell the provider which report we're viewing. Triggers fetch. */
  setSlug: (slug: string | null) => void;
}

const Ctx = createContext<ReportContextValue | null>(null);

export function ReportProvider({ children }: { children: ReactNode }) {
  const [slug, setSlug] = useState<string | null>(null);
  const [report, setReport] = useState<ReportDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch when slug changes. Reset state in between to avoid showing stale
  // data from the previous report while a new one loads.
  useEffect(() => {
    if (!slug) {
      setReport(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getReport(slug)
      .then((doc) => {
        if (!cancelled) setReport(doc);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <Ctx.Provider value={{ slug, report, loading, error, setSlug }}>
      {children}
    </Ctx.Provider>
  );
}

export function useReport(): ReportContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useReport must be used inside <ReportProvider>");
  return v;
}
